import * as Logger from "js-logger";
import { Signal } from "signals";
import ReelComponent from "slotworks/components/matrix/reel/reel-component";
import AbstractReelTransition from "slotworks/components/matrix/reel/transition-behaviours/abstract-reel-transition";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import Layers from "slotworks/graphics/layers/layers";
import { AnticipationInfo } from "slotworks/model/gameplay/records/spin-record";
import { SoundEvent } from "slotworks/sound/sound-events";
import SoundManager from "slotworks/sound/sound-manager";
import Contract from "slotworks/utils/contract";
import { RandomRangeInt } from "slotworks/utils/math/random";
import { parallel } from "slotworks/utils/race-utils";
import { sequence } from "slotworks/utils/race-utils";
import Timer from "slotworks/utils/timer";

export default class AnimateReelTransition extends AbstractReelTransition {

    public static ANTICIPATION_DELAY: number = 500;
    public static anticipationLayer: Layers;

    private startGap: number = 100;
    private stopGap: number = 100;
    private quickStopGap: number = 50;

    private anticipations: AnticipationInfo;
    private anticipationAnimations: AnimatedSprite[] = [];
    private heldReels: number[];
    private anticipating: boolean;

    private pendingLands: number = 0;
    private resolveOnLand: Function;

    private stopTimers: number[];

    constructor(startGap: number, stopGap: number, quickStopGap: number) {
        super();

        this.onReelLand = new Signal();

        this.startGap = startGap;
        this.stopGap = stopGap;
        this.quickStopGap = quickStopGap;
    }

    public init(reels: ReelComponent[], stops: number[], reelset: string[][]) {
        super.init(reels, stops, reelset);

        this.cancelSpin();
    }

    public start(reelset?: string[][], jumpStart: boolean = false): Contract<void> {
        SoundManager.event(SoundEvent.reel_transition_start);

        const startFuncs = [];

        let n = -1;
        for (let i = 1; i <= this.reels.length; i++) {
            startFuncs.push(() => new Contract<void>((resolve) => {
                Timer.setTimeout(() => {
                    if (this.heldReels && this.heldReels.indexOf(i) > -1) {
                        resolve(null);
                    } else {
                        SoundManager.event(SoundEvent.reel_N_spin_start, i.toString());
                        SoundManager.event(SoundEvent.reel_any_spin_start);

                        this.spinReel(i).then(() => resolve(null));
                        n++;
                    }
                }, jumpStart ? 0 : this.startGap * n);
            }));
        }

        return parallel(startFuncs);
    }

    public stop(stops: number[], reelset: string[][], quickSpin: boolean, anticipation: AnticipationInfo): Contract<void> {

        this.anticipating = false;
        this.anticipations = anticipation;

        const stopFuncs: Array<() => Contract<void>> = [];

        this.stopTimers = [];

        for (let i = 1; i <= this.reels.length; i++) {
            const isHeld = this.heldReels && this.heldReels.indexOf(i) > -1;
            const anticipate = anticipation.reelAnticipations[i - 1];

            stopFuncs.push(() => new Contract<void>((resolve) => {

                let stopDelay = quickSpin ? this.quickStopGap : this.stopGap;

                if (!isHeld) {

                    if (anticipate) {
                        stopDelay += AnimateReelTransition.ANTICIPATION_DELAY;
                        this.anticipate(i);
                    }

                    const timer = Timer.setTimeout(() => {

                        this.stopAnticipation(i);

                        this.stopReel(i, stops, reelset, anticipation).then(() => {
                            for (const symbol of this.reels[i - 1].getSymbols()) {
                                symbol.setSymbol(reelset[i - 1][stops[i - 1]]);
                                symbol.updateTransform();
                                symbol.stop().execute();
                                if (anticipation.landSymbols[i - 1].indexOf(symbol.symbolId) > -1) {
                                    symbol.land().execute();
                                }
                            }

                            SoundManager.event(SoundEvent.reel_N_spin_end, i.toString());
                            SoundManager.event(SoundEvent.reel_any_spin_end);

                            resolve(null);
                        });
                    }, stopDelay);

                    this.stopTimers.push(timer);
                } else {
                    resolve(null);
                }
            }));
        }

        return new Contract<void>((resolve) => {
            sequence(stopFuncs).then(() => {
                SoundManager.event(SoundEvent.reel_transition_end);

                this.stopAnticipations();
                resolve(null);
            });
        });
    }

    public skip(stops: number[], reelset: string[][]): Contract<void> {
        SoundManager.event(SoundEvent.reel_transition_skip);

        for (const timer of this.stopTimers) {
            Timer.clearTimeout(timer);
        }

        const stopFuncs: Array<() => Contract<void>> = [];

        for (let i = 1; i <= this.reels.length; i++) {
            if (this.getReelSpinAnim(i).visible) {
                stopFuncs.push(() => this.stopReel(i, stops, reelset, this.anticipations));
            }
        }

        return new Contract<void>((resolve) => {
            sequence(stopFuncs).then(() => {
                SoundManager.event(SoundEvent.reel_transition_skip_end);

                this.stopAnticipations();
                if (this.pendingLands === 0) {
                    resolve(null);
                } else {
                    this.resolveOnLand = resolve;
                }
            });
        });
    }

    public jump(stops: number[], reelset: string[][]) {
        this.cancelSpin();

        for (let i = 0; i < stops.length; i++) {
            for (const symbol of this.reels[i].getSymbols()) {
                symbol.setSymbol(reelset[i][stops[i]]);
            }
        }
    }

    public hold(reelIndexes?: number[]) {
        this.heldReels = reelIndexes;
    }

    public update() {
        super.update();
    }

    private spinReel(index: number) {
        const reelSpin = this.getReelSpinAnim(index);
        reelSpin.gotoAndPlay(RandomRangeInt(1, reelSpin.totalFrames - 1));
        reelSpin.visible = true;

        this.setReelSymbolsVisible(index, false);

        return Contract.empty();
    }

    private stopReel(index: number, stops: number[], reelset: string[][], anticipation: AnticipationInfo) {
        const reelSpin = this.getReelSpinAnim(index);
        reelSpin.stop();
        reelSpin.visible = false;

        this.setReelSymbolsVisible(index, true);

        for (const symbol of this.reels[index - 1].getSymbols()) {
            symbol.setSymbol(reelset[index - 1][stops[index - 1]]);
            symbol.stop().execute();
            if (anticipation && anticipation.landSymbols[index - 1].indexOf(symbol.symbolId) > -1) {
                this.pendingLands++;
                symbol.land().then(() => {
                    this.pendingLands--;
                    if (this.pendingLands <= 0 && this.resolveOnLand) {
                        this.resolveOnLand();
                        this.resolveOnLand = null;
                    }
                });
            }
        }

        return Contract.empty();
    }

    private anticipate(reelIndex: number) {

        if (!this.anticipating) {
            this.anticipating = true;
            SoundManager.event(SoundEvent.reel_transition_anticipate);
        }
        SoundManager.event(SoundEvent.reel_any_anticipate_start);
        SoundManager.event(SoundEvent.reel_N_anticipate_start, reelIndex.toString());

        const anticipationLayer = AnimateReelTransition.anticipationLayer || Layers.SymbolAnimations;
        const anticipationAnimation = anticipationLayer.getAnimatedSprite("anticipation_" + reelIndex.toString());
        if (anticipationAnimation) {
            anticipationAnimation.visible = true;
            anticipationAnimation.loop = true;
            anticipationAnimation.play();
            this.anticipationAnimations[reelIndex - 1] = anticipationAnimation;
        }
    }

    private stopAnticipation(reelIndex: number) {

        const anticipation = (this.anticipationAnimations.length > reelIndex - 1) ? this.anticipationAnimations[reelIndex - 1] : null;
        if (anticipation) {
            SoundManager.event(SoundEvent.reel_any_anticipate_end);
            SoundManager.event(SoundEvent.reel_N_anticipate_end, reelIndex.toString());

            anticipation.stop();
            anticipation.visible = false;
        }
        this.anticipationAnimations[reelIndex - 1] = null;
    }

    private stopAnticipations() {
        for (let i = 1; i <= this.anticipationAnimations.length; i++) {
            this.stopAnticipation(i);
        }
    }

    private getReelSpinAnim(index: number) {
        const anim = Layers.MatrixContent.getAnimatedSprite("reelspin_" + index.toString());
        if (!anim) {
            Logger.error("reelspin_" + index.toString() + " does not exist");
        }

        return anim;
    }

    private setReelSymbolsVisible(reelIndex: number, visible: boolean) {
        const symbols = this.reels[reelIndex - 1].getSymbols();

        for (const symbol of symbols) {
            symbol.setVisible(visible);
            symbol.updateTransform();
        }
    }

    private cancelSpin() {
        for (let i = 1; i <= this.reels.length; i++) {
            const reelSpin = this.getReelSpinAnim(i);
            reelSpin.stop();
            reelSpin.visible = false;
            this.setReelSymbolsVisible(i, true);
        }
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/components/matrix/reel/transition-behaviours/animate/animate-reel-transition.ts